// Modal + confirm helpers (just-in-time UI primitives, first needed by the
// categories rename/delete flows; reused thereafter). CSP-safe: built with the
// DOM builder, styled by external CSS. Backdrop click + Esc close; focus moves
// into the dialog and returns to the opener on close.
import { h, type Child } from '../core/dom';

export function modal(opts: { title?: string; body: Child | Child[]; onClose?: () => void }): () => void {
	const onKey = (e: KeyboardEvent) => {
		if (e.key === 'Escape') close();
	};
	const card = h(
		'div',
		{ class: 'modal-card', role: 'dialog', 'aria-modal': 'true' },
		opts.title ? h('h2', { class: 'modal-title' }, opts.title) : null,
		...(Array.isArray(opts.body) ? opts.body : [opts.body])
	);
	const overlay = h(
		'div',
		{ class: 'modal-overlay', onclick: (e: MouseEvent) => { if (e.target === overlay) close(); } },
		card
	);
	let closed = false;
	const opener = document.activeElement as HTMLElement | null;
	function close(): void {
		if (closed) return;
		closed = true;
		overlay.remove();
		document.removeEventListener('keydown', onKey);
		opts.onClose?.();
		if (opener?.isConnected) opener.focus();
	}
	document.body.append(overlay);
	document.addEventListener('keydown', onKey);
	queueMicrotask(() => (card.querySelector('input, select, button') as HTMLElement | null)?.focus());
	return close;
}

export function confirmDialog(message: string, confirmLabel = 'Confirm'): Promise<boolean> {
	return new Promise((resolve) => {
		let decided = false;
		const finish = (v: boolean) => {
			if (decided) return;
			decided = true;
			close();
			resolve(v);
		};
		const close = modal({
			body: [
				h('p', {}, message),
				h(
					'div',
					{ class: 'row spacer-end' },
					h('button', { class: 'btn btn-secondary', onclick: () => finish(false) }, 'Cancel'),
					h('button', { class: 'btn btn-primary', onclick: () => finish(true) }, confirmLabel)
				),
			],
			onClose: () => finish(false),
		});
	});
}
